import { nameMatchesToken, type TableFilters } from "@aera/data-table";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  isReceivableAddressItem,
  isReceivableSlipItem,
} from "@/lib/pendingOrders";
import {
  RECEIVABLE_DISMISSED_FILTER_LABEL,
  receivableMatchesStatusFilter,
  isReceivableArchivedDisplayStatus,
} from "@/lib/receivableDisplayStatus";

const RECEIVABLE_AMOUNT_SORT_KEYS = new Set([
  "slip_amount",
  "amount",
  "amount_original",
  "amount_allocated",
  "amount_paid",
  "amount_remaining",
  "transfer_amount",
  "total_amount",
]);

const RECEIVABLE_DATETIME_SORT_KEYS = new Set([
  "transfer_date",
  "transfer_datetime",
  "created_at",
  "expires_at",
  "recorded_at",
  "record_date",
  "queue_left_at",
]);

function isArchivedStatusFilter(status: string): boolean {
  return (
    status === "หมดอายุ" ||
    status === RECEIVABLE_DISMISSED_FILTER_LABEL ||
    status === "ลบรายการ"
  );
}

function toBangkokDateKey(input: string | null | undefined): string | null {
  if (!input?.trim()) return null;
  const date = parseDatetimeInput(input.trim());
  if (!date) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function parseDatetimeInput(input: string): Date | null {
  if (/^\d{10,13}$/.test(input)) {
    const numeric = Number(input);
    const ms = numeric > 1e12 ? numeric : numeric * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isReceivableAnnouncementItem(item: ReceivableListItem): boolean {
  return (item.kindLabel ?? "").includes("ประกาศ");
}

function getReceivableSearchText(item: ReceivableListItem): string {
  const parts = [
    item.title,
    item.detail,
    item.kindLabel,
    item.amountLabel,
    ...item.fields.map((field) => field.value),
  ];
  return parts
    .filter((part) => part && part !== "—")
    .join(" ")
    .toLowerCase();
}

function getReceivablePrimaryDateKey(item: ReceivableListItem): string | null {
  return toBangkokDateKey(item.createdAt) ?? toBangkokDateKey(item.transferDate) ?? null;
}

/** Date chips use เวลาสร้าง on every tab, including dismissed/expired. */
function getReceivableFilterDateKey(
  item: ReceivableListItem,
  _statusFilter: string,
): string | null {
  return getReceivablePrimaryDateKey(item);
}

function getReceivableFieldRaw(
  item: ReceivableListItem,
  key: string,
): string | null {
  if (key === "transfer_date" || key === "transfer_datetime") {
    return item.transferDate;
  }
  if (key === "created_at" || key === "recorded_at" || key === "record_date") {
    return item.createdAt;
  }
  if (key === "expires_at") {
    const field = item.fields.find((entry) => entry.key === "expires_at");
    return field?.value ?? null;
  }
  const field = item.fields.find((entry) => entry.key === key);
  return field?.value ?? null;
}

function parseReceivableAmountSort(value: string | null | undefined): number {
  if (!value) return 0;
  const numeric = Number(value.replace(/[฿,\s]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
}

function getReceivableSortValue(
  item: ReceivableListItem,
  key: string,
): number | string {
  if (RECEIVABLE_AMOUNT_SORT_KEYS.has(key)) {
    const raw = getReceivableFieldRaw(item, key);
    return parseReceivableAmountSort(raw ?? item.amountLabel);
  }

  if (RECEIVABLE_DATETIME_SORT_KEYS.has(key)) {
    const raw = getReceivableFieldRaw(item, key);
    if (!raw) return 0;
    const date = parseDatetimeInput(raw);
    return date?.getTime() ?? 0;
  }

  return (getReceivableFieldRaw(item, key) ?? "").toLowerCase();
}

function compareReceivableSortValues(
  left: number | string,
  right: number | string,
): number {
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  return String(left).localeCompare(String(right), "th");
}

export function filterAndSortReceivableItems(
  items: ReceivableListItem[],
  filters: Partial<TableFilters>,
): ReceivableListItem[] {
  const query = filters.q?.trim().toLowerCase() ?? "";
  const nameToken = filters.nameToken?.trim() ?? "";
  const status = filters.status?.trim() ?? "";
  const category = filters.category?.trim() ?? "";
  const from = filters.from?.trim() ?? "";
  const to = filters.to?.trim() ?? "";
  const requestedSortKey = filters.sort?.trim() ?? "created_at";
  // Archived views: transfer/expiry columns are often empty — sort by when they left the queue.
  const sortKey =
    isArchivedStatusFilter(status) &&
    (requestedSortKey === "transfer_date" ||
      requestedSortKey === "transfer_datetime" ||
      requestedSortKey === "expires_at")
      ? "created_at"
      : requestedSortKey;
  const sortDir = filters.dir === "asc" ? "asc" : "desc";

  let result = items.filter((item) => {
    if (query && !getReceivableSearchText(item).includes(query)) {
      return false;
    }

    if (nameToken && !nameMatchesToken(item.title, nameToken)) {
      return false;
    }

    if (
      status === "ค้าง" ||
      status === "รอตรวจ" ||
      status === "รายการที่ลบ" ||
      status === "หมดอายุ" ||
      status === "รับบางส่วน" ||
      status === "เหลือรอจัดสรร"
    ) {
      if (!receivableMatchesStatusFilter(item, status)) {
        return false;
      }
    } else if (isReceivableArchivedDisplayStatus(item)) {
      // Default list: hide dismissed / expired until selected in status filter.
      return false;
    }

    if (category === "สลิป" && !isReceivableSlipItem(item)) {
      return false;
    }
    if (category === "ที่อยู่" && !isReceivableAddressItem(item)) {
      return false;
    }
    if (category === "ประกาศ" && !isReceivableAnnouncementItem(item)) {
      return false;
    }

    if (from || to) {
      const dateKey = getReceivableFilterDateKey(item, status);
      if (!dateKey) return false;
      if (from && dateKey < from) return false;
      if (to && dateKey > to) return false;
    }

    return true;
  });

  result = [...result].sort((left, right) => {
    const cmp = compareReceivableSortValues(
      getReceivableSortValue(left, sortKey),
      getReceivableSortValue(right, sortKey),
    );
    return sortDir === "asc" ? cmp : -cmp;
  });

  return result;
}

export function createDefaultReceivableFilters(
  defaultSort: { col: string; dir: "asc" | "desc" },
): TableFilters {
  return {
    status: null,
    from: null,
    to: null,
    q: null,
    sort: defaultSort.col,
    dir: defaultSort.dir,
    category: null,
    nameToken: null,
  };
}
